import { Fragment, useState, useEffect } from "react";
import Grid2 from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import DeviceDealerList from "./deviceDealersList";
import { getCountryCodeISO } from "../../utils/general";
import {
  getDeviceDealerInfoByPSN,
  getDeviceDealerInfoByDealerId,
} from "../../action/deviceDealer";
import DisplayDeviceDealerDeatils from "./deviceDealerDetailsPage";
import customConfig from "../../config";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import { SearchSectionLabel, SectionHeading } from "../Common/Typography/AppTypography";
import SearchIcon from "@mui/icons-material/Search";
import { selectMenuProps } from "../../utils/selectMenuProps";
import { useDesignTokens } from "../../theme/useDesignTokens";
import { useSidebarContext } from "../../context/SidebarContext";
import {
  ACTION_BUTTON_MIN_WIDTH,
  INPUT_DESKTOP_MAX_WIDTH,
} from "../../constants/uiConstants";

const searchByLabels = {
  dealerId: "Dealer Id",
  productSerialNumber: "Product Serial Number",
  dealerCountry: "Country",
};
const GetDealerInfo = ({
  allowedCountries,
  setAlert,
  onEditDeviceDealerDetails,
  showUpdatedDealerInfo,
  userRoleInfo,
}) => {
  const fieldHeight = 45;
  const theme = useTheme();
  const { isAnySidebarStateExpanded } = useSidebarContext();
  const { colors } = useDesignTokens();
  const primaryColor = colors?.primary?.main || theme.palette.primary.main;
  const defaultBorderColor = theme.palette.divider;
  const selectIconColor = theme.palette.action.active;
  const focusOutlineSx = {
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${defaultBorderColor} !important`,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${primaryColor} !important`,
    },
    "& .MuiOutlinedInput-root.Mui-expanded .MuiOutlinedInput-notchedOutline, & .Mui-expanded .MuiOutlinedInput-notchedOutline":
      {
        borderColor: `${defaultBorderColor} !important`,
        borderWidth: "1px",
      },
  };

  const [dealerSearchBy, setDealerSearchBy] = useState("dealerId");
  const [dealerSearchByID, setDealerSearchByID] = useState("");
  const [searchButtonClicked, setSearchButtonClicked] = useState(false);
  const [dealerSearchByCountry, setDealerSearchByCountry] = useState("");
  const [dealerSearchByPSN, setDealerSearchByPSN] = useState("");
  const [fieldPlaceholder, setFieldPlaceholder] = useState("Enter Dealer Id");

  const [deviceDealerInfo, setDeviceDealerInfo] = useState({});

  useEffect(() => {
    setFieldPlaceholder(
      `Enter ${searchByLabels[dealerSearchBy] || "value"}`,
    );
  }, [dealerSearchBy]);

  useEffect(() => {
    setDeviceDealerInfo({});
  }, [
    dealerSearchBy,
    dealerSearchByCountry,
    dealerSearchByID,
    dealerSearchByPSN,
    searchButtonClicked,
  ]);

  useEffect(() => {
    if (showUpdatedDealerInfo) {
      setDeviceDealerInfo({});
    }
  }, [showUpdatedDealerInfo]);

  function handleEditDeviceDealerDetails(deviceDealerDetails) {
    onEditDeviceDealerDetails(deviceDealerDetails);
  }

  function handleSearchButtonClicked() {
    setSearchButtonClicked(true);

    if (dealerSearchBy === "productSerialNumber") {
      if (dealerSearchByPSN && dealerSearchByPSN.length !== 0) {
        getDeviceDealerInfoByPSN(dealerSearchByPSN)
          .then((data) => {
            setDeviceDealerInfo(data);
          })
          .catch((err) => {
            setAlert(
              err?.response?.data?.message ?? "Failed to Get Dealer Details",
              "danger",
            );
          });
      } else {
        setAlert("Invalid Product Serial Number", "danger");
      }
    } else if (dealerSearchBy === "dealerId") {
      if (dealerSearchByID && dealerSearchByID.length !== 0) {
        getDeviceDealerInfoByDealerId(dealerSearchByID)
          .then((data) => {
            setDeviceDealerInfo(data);
          })
          .catch((err) => {
            setAlert(
              err?.response?.data?.message ?? "Failed to Get Dealer Details",
              "danger",
            );
          });
      } else {
        setAlert("Invalid Dealer ID", "danger");
      }
    } else {
      if (
        !dealerSearchByCountry ||
        (dealerSearchByCountry && dealerSearchByCountry === "Select Country") ||
        (dealerSearchByCountry && dealerSearchByCountry.length === 0)
      ) {
        setAlert("Invalid Country", "danger");
      }
    }
  }

  function handleSearchFormSubmit(e) {
    e.preventDefault();
    handleSearchButtonClicked();
  }

  function handleDeleteDeviceDealerDetails() {
    setDeviceDealerInfo({});
  }

  const countryOptions = [
    { label: "Select Country", value: "" },
    ...allowedCountries
      .filter((c) => getCountryCodeISO(c.toUpperCase()))
      .map((c) => ({
        label: getCountryCodeISO(c.toUpperCase()),
        value: getCountryCodeISO(c.toUpperCase()),
      })),
  ];

  return (
    <Fragment>
      <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
        <Grid2 container spacing={2}>
          <Grid2
            size={12}
            sx={{
              p: 2,
              borderRadius: 1,
              boxShadow: 2,
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                rowGap: 2,
                alignItems: "center",
              }}
            >
              <SectionHeading sx={{ mb: 0 }}>Search Device Dealer</SectionHeading>
              <Box
                component="form"
                onSubmit={handleSearchFormSubmit}
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: isAnySidebarStateExpanded
                      ? "90px 190px minmax(0, 1fr)"
                      : "90px 200px minmax(180px, 260px) 96px",
                    lg: `90px ${INPUT_DESKTOP_MAX_WIDTH}px minmax(220px, 320px) ${ACTION_BUTTON_MIN_WIDTH}px`,
                  },
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "start",
                }}
              >
                <SearchSectionLabel>Search By</SearchSectionLabel>
                <FormInput
                  type="select"
                  size="small"
                  fullWidth
                  name="dealerSearchBy"
                  value={dealerSearchBy}
                  onChange={(e) => {
                    setSearchButtonClicked(false);
                    setDealerSearchBy(e.target.value);
                  }}
                  options={[
                    { label: "Dealer Id", value: "dealerId" },
                    {
                      label: "Product Serial Number",
                      value: "productSerialNumber",
                    },
                    { label: "Country", value: "dealerCountry" },
                  ]}
                  MenuProps={selectMenuProps}
                  sx={{
                    minWidth: {
                      xs: "100%",
                      md: isAnySidebarStateExpanded ? 190 : 200,
                      lg: INPUT_DESKTOP_MAX_WIDTH,
                    },
                    maxWidth: {
                      xs: "100%",
                      md: isAnySidebarStateExpanded ? 190 : 200,
                      lg: INPUT_DESKTOP_MAX_WIDTH,
                    },
                    "& .MuiOutlinedInput-root": { height: fieldHeight },
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      minHeight: `${fieldHeight}px !important`,
                    },
                    "& .MuiSelect-icon": {
                      color: selectIconColor,
                    },
                    "& .Mui-focused .MuiSelect-icon": {
                      color: primaryColor,
                    },
                    ...focusOutlineSx,
                  }}
                />

                {dealerSearchBy === "dealerId" && (
                  <FormInput
                    type="text"
                    name="dealerSearchField"
                    placeholder={fieldPlaceholder}
                    value={dealerSearchByID}
                    onChange={(e) => {
                      setDealerSearchByID(e.target.value);
                      setSearchButtonClicked(false);
                    }}
                    size="small"
                    fullWidth
                    margin="none"
                    startAdornment={<SearchIcon fontSize="small" />}
                    sx={{
                      margin: 0,
                      maxWidth: { xs: "100%", lg: 320 },
                      "& .MuiOutlinedInput-root": {
                        height: fieldHeight,
                        minHeight: fieldHeight,
                        maxHeight: fieldHeight,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: `1px solid ${defaultBorderColor} !important`,
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderWidth: "1px !important",
                      },
                      "& .MuiInputBase-input": {
                        height: `${fieldHeight}px !important`,
                        boxSizing: "border-box",
                      },
                      ...focusOutlineSx,
                    }}
                  />
                )}
                {dealerSearchBy === "productSerialNumber" && (
                  <FormInput
                    type="text"
                    name="dealerSearchField"
                    placeholder={fieldPlaceholder}
                    value={dealerSearchByPSN}
                    onChange={(e) => {
                      setDealerSearchByPSN(e.target.value);
                      setSearchButtonClicked(false);
                    }}
                    size="small"
                    fullWidth
                    margin="none"
                    startAdornment={<SearchIcon fontSize="small" />}
                    sx={{
                      margin: 0,
                      maxWidth: { xs: "100%", lg: 320 },
                      "& .MuiOutlinedInput-root": {
                        height: fieldHeight,
                        minHeight: fieldHeight,
                        maxHeight: fieldHeight,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: `1px solid ${defaultBorderColor} !important`,
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderWidth: "1px !important",
                      },
                      "& .MuiInputBase-input": {
                        height: `${fieldHeight}px !important`,
                        boxSizing: "border-box",
                      },
                      ...focusOutlineSx,
                    }}
                  />
                )}
                {dealerSearchBy === "dealerCountry" && (
                  <FormInput
                    type="select"
                    size="small"
                    fullWidth
                    name="dealerSearchByCountry"
                    value={dealerSearchByCountry}
                    displayEmpty
                    onChange={(e) => {
                      setDealerSearchByCountry(e.target.value);
                      setSearchButtonClicked(false);
                    }}
                    options={countryOptions}
                    MenuProps={selectMenuProps}
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      "& .MuiOutlinedInput-root": { height: fieldHeight },
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        minHeight: `${fieldHeight}px !important`,
                      },
                      "& .MuiSelect-icon": {
                        color: selectIconColor,
                      },
                      "& .Mui-focused .MuiSelect-icon": {
                        color: primaryColor,
                      },
                      ...focusOutlineSx,
                    }}
                  />
                )}

                <AppButton
                  type="submit"
                  variant="primary"
                  size="medium"
                  sx={{
                    minWidth: { xs: ACTION_BUTTON_MIN_WIDTH, md: 96, lg: ACTION_BUTTON_MIN_WIDTH },
                    height: fieldHeight,
                    px: { xs: 1.5, md: 1.25, lg: 1.5 },
                    width: { xs: "100%", md: "auto" },
                    justifySelf: {
                      xs: "start",
                      md: isAnySidebarStateExpanded ? "end" : "start",
                      lg: "start",
                    },
                    gridColumn: {
                      xs: "auto",
                      md: isAnySidebarStateExpanded ? "1 / -1" : "auto",
                      lg: "auto",
                    },
                  }}
                >
                  Search
                </AppButton>
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </Box>

      <Box>
        {searchButtonClicked &&
          dealerSearchBy === "dealerCountry" &&
          dealerSearchByCountry &&
          dealerSearchByCountry !== "Select Country" &&
          dealerSearchByCountry.length > 0 && (
            <DeviceDealerList
              countryCodeFilter={dealerSearchByCountry}
              setAlert={setAlert}
              onEditDeviceDealerDetails={handleEditDeviceDealerDetails}
              showUpdatedDealerInfo={showUpdatedDealerInfo}
              userRoleInfo={userRoleInfo}
            />
          )}

        {searchButtonClicked &&
          dealerSearchBy === "productSerialNumber" &&
          dealerSearchByPSN &&
          Object.keys(deviceDealerInfo).length > 0 && (
            <DisplayDeviceDealerDeatils
              deviceDealerDetails={deviceDealerInfo}
              displayEditDeleteButton={
                userRoleInfo.level > 4 ||
                userRoleInfo.roles.includes(customConfig.roleCloudAdmin)
              }
              onEditDeviceDealerDetails={handleEditDeviceDealerDetails}
              showUpdatedDealerInfo={showUpdatedDealerInfo}
              onDeleteDeviceDealerDetails={handleDeleteDeviceDealerDetails}
              setAlert={setAlert}
            />
          )}

        {searchButtonClicked &&
          dealerSearchBy === "dealerId" &&
          dealerSearchByID &&
          Object.keys(deviceDealerInfo).length > 0 && (
            <DisplayDeviceDealerDeatils
              deviceDealerDetails={deviceDealerInfo}
              displayEditDeleteButton={
                userRoleInfo.level > 4 ||
                userRoleInfo.roles.includes(customConfig.roleCloudAdmin)
              }
              onEditDeviceDealerDetails={handleEditDeviceDealerDetails}
              onDeleteDeviceDealerDetails={handleDeleteDeviceDealerDetails}
              setAlert={setAlert}
            />
          )}
      </Box>
    </Fragment>
  );
};

export default GetDealerInfo;
